"use client";

/**
 * Global network-drop banner (C12).
 *
 * When the browser goes offline, autosaves, generation polls, and uploads all
 * start failing quietly — the user sees stalls with no explanation. This shows
 * a fixed banner the moment connectivity drops and clears it on reconnect, so
 * failures read as "you're offline" instead of "the app is broken."
 *
 * Uses the `online`/`offline` window events + `navigator.onLine`. Mount-gated
 * so SSR (which has no navigator) and the first client render agree → no #418.
 */
import { useEffect, useState } from "react";

export function OfflineBanner() {
	// Start optimistic (online) on both server and first client render so
	// hydration matches; correct it in the mount effect.
	const [online, setOnline] = useState(true);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		setOnline(navigator.onLine);
		const goOnline = () => setOnline(true);
		const goOffline = () => setOnline(false);
		window.addEventListener("online", goOnline);
		window.addEventListener("offline", goOffline);
		return () => {
			window.removeEventListener("online", goOnline);
			window.removeEventListener("offline", goOffline);
		};
	}, []);

	if (!mounted || online) return null;

	return (
		<div
			role="status"
			aria-live="polite"
			className="fixed inset-x-0 top-0 z-[200] flex items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-center text-xs font-medium text-black shadow-md"
		>
			<span
				className="inline-block size-2 shrink-0 rounded-full bg-black/70"
				aria-hidden
			/>
			You&apos;re offline — changes won&apos;t save and generation is paused until you reconnect.
		</div>
	);
}
